import React, { FunctionComponent, useMemo, useState } from 'react';
import { range } from 'lodash';
import { DealerType } from '@upstart/harmony/lib/legos/constants';
import addCircleImage from '../../../assets/add-circle.png';
import closeImage from '../../../assets/close.png';
import logoPreviewImage from '../../../assets/guides/logo-preview.png';
import { DealerLaunchStepProps } from '../types';
import { TextField } from '../../Input/TextField/TextField';
import { Dealer } from '../../../types/dealerTypes';
import { Button } from '../../Input/Button/Button';
import { FormLogoUpload } from '../../Input/ImageUpload/ImageUpload';
import { Select } from '../../Input/Select/Select';
import { BRANDS } from '../../../utils/constants';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import { getDefaultBrands } from '../defaults';
import { PreviewGuide } from '../../QuickGuide/QuickGuide';
import { DealerLaunchFormCardLayout } from '../DealerLaunchFormCardLayout';
import { urlValidator } from '../../../utils/validation';
import { Radio } from '../../Input/Radio/Radio';
import styles from '../styles.module.scss';

const MIN_BRAND_COUNT = 1;
const MAX_BRAND_COUNT = 4;

const BRAND_SELECT_OPTIONS = BRANDS.map((brand) => ({
  label: brand,
  value: brand,
}));

export const DealerLaunchAboutInfo: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  nextStep,
  prevStep,
}) => {
  const defaultBrands = useMemo(
    () => getDefaultBrands(dealer).slice(0, MAX_BRAND_COUNT),
    [dealer],
  );
  const formDefaultBrand = dealer.brand?.length ? dealer.brand : defaultBrands;
  const form = useLaunchpadForm<Dealer>({
    defaultValues: { ...dealer, brand: formDefaultBrand },
  });

  const [brandCount, setBrandCount] = useState(
    Math.min(formDefaultBrand.length || MIN_BRAND_COUNT, MAX_BRAND_COUNT),
  );

  const deleteBrand = (index: number) => {
    // move all later brands to fill in the deleted brand
    for (let i = index; i < brandCount - 1; i += 1) {
      form.setValue(`brand[${i}]`, form.getValues(`brand[${i + 1}]`));
    }
    form.clearErrors(`brand[${index}]`);
    form.clearErrors(`brand[${brandCount - 1}]`);
    setBrandCount(brandCount - 1);
  };

  const onSubmit = async (data: Dealer) => {
    const fixedData = { ...data };
    // force brands to only include the visible list
    fixedData.brand = dealer.isIndependent
      ? BRAND_SELECT_OPTIONS.map((b) => b.value)
      : data.brand?.slice(0, brandCount);

    // validate against duplicates
    const duplicateBrandIndex = fixedData.brand?.findIndex((brand, index) =>
      fixedData.brand?.slice(index + 1).includes(brand),
    );
    if (duplicateBrandIndex !== -1) {
      form.setError(`brand[${duplicateBrandIndex}]`, {
        message: 'Duplicate brands are not allowed',
      });
      return;
    }

    // if the logo changed, save it under `logoFile` so that is gets handled as a file upload in the backend
    if (fixedData.logo && fixedData.logo !== dealer.logo) {
      const dataWithLogoFile = {
        ...fixedData,
        logo: undefined,
        logoFile: fixedData.logo,
      };
      await nextStep(dataWithLogoFile);
    } else {
      await nextStep(fixedData);
    }
  };

  const renderLogoUploadLabel = () => (
    <>
      Upload Your Logo
      <div className="spacer" />
      <PreviewGuide action="Show Where This is Used" guideTitle="Logo Upload">
        <div>
          <div className="modal-subtitle">Where is this being used?</div>
          Your logo will be placed into our product so your customers always
          know who they&apos;re buying from.
          <img src={logoPreviewImage} alt="Website Preview" />
          <br />
          <br />
          <b>Best Dimensions:</b> 1200 х 628 px
          <br />
          <br />
          <b>Image Background:</b> Try a white, or transparent background.
          <br />
          <br />
          <div className="modal-subtitle">Where do I find my logo?</div>
          Contact your web provider or marketing team for your logo. Logos can
          also be saved from your website by right clicking your logo and
          clicking &apos;save image&apos;.
        </div>
      </PreviewGuide>
    </>
  );

  return (
    <DealerLaunchFormCardLayout
      className={styles.aboutInfoStep}
      form={form}
      title="Tell Us About Your Dealership"
      subTitle="We want to know all about your dealership so that our online and instore solutions fit seamlessly with your dealership's brand."
      cardTitle="Dealer Information"
      prevStep={prevStep}
      onFormSubmit={onSubmit}
    >
      <div className={styles.stepRow}>
        <TextField
          label="Your Dealership's Name"
          name="name"
          width="md"
          form={form}
          validation={{ required: true }}
        />
        <TextField
          label="Your Dealership’s Website"
          name="dealerWebsiteUrl"
          width="md"
          form={form}
          validation={{ required: true, validate: urlValidator }}
        />
      </div>
      <TextField
        label="Your Dealership’s Website Privacy Policy"
        tooltip="Your website’s privacy policy can typically be found as a link in your website footer at the bottom of the page."
        name="privacyPolicyUrl"
        width="md"
        form={form}
        validation={{ required: true, validate: urlValidator }}
      />
      {!dealer.isIndependent && (
        <>
          <div className={styles.stepRow}>
            {range(brandCount).map((brandIndex) => (
              <Select
                key={brandIndex}
                label={
                  <>
                    Brand {brandIndex ? `(${brandIndex + 1})` : null}
                    {brandCount > MIN_BRAND_COUNT && (
                      <Button
                        className={styles.deleteBrandButton}
                        title="Delete Brand"
                        variant="minimal"
                        onClick={() => deleteBrand(brandIndex)}
                      >
                        <img src={closeImage} alt="Delete" />
                      </Button>
                    )}
                  </>
                }
                // If names have issues with forms, use this
                name={`brand[${brandIndex}]` as any}
                width="sm"
                form={form}
                validation={{ required: true }}
                options={BRAND_SELECT_OPTIONS}
                addEmptyOption
              />
            ))}
            {brandCount < MAX_BRAND_COUNT && (
              <Button
                variant="minimal"
                onClick={() => setBrandCount(brandCount + 1)}
              >
                <img className={styles.addIcon} src={addCircleImage} alt="" />{' '}
                Add New Brand
              </Button>
            )}
          </div>
          <div className={styles.byoSelection}>
            <div>Do you want to enable build and price for these brands?</div>
            <Radio
              className="flex-column"
              name="dealerType"
              options={[
                {
                  label: 'Yes',
                  value: DealerType.BYO_ENABLED,
                },
                {
                  label: 'No',
                  value: DealerType.Default,
                },
              ]}
              form={form}
              validation={{
                validate: (val: any) => {
                  if (typeof val !== 'string') return 'A selection is required';
                  return true;
                },
              }}
            />
          </div>
        </>
      )}

      <FormLogoUpload
        label={renderLogoUploadLabel()}
        name="logo"
        form={form}
        required
      />
    </DealerLaunchFormCardLayout>
  );
};
